import React, { useEffect, useState } from 'react';
import { ServiceRecord, ServiceRecordStatus, ServiceRecordStatusFilter, STATUS_MAP } from '../types';
import { formatPricePLN } from '../utils/formatters';
import { StatusFilter } from './StatusFilter';

interface RecordListProps {
  records: ServiceRecord[];
  selectedUuid?: string;
  onSelectRecord: (record: ServiceRecord) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: ServiceRecordStatusFilter;
  onStatusFilterChange: (status: ServiceRecordStatusFilter) => void;
  showArchived: boolean;
  onToggleShowArchived: (show: boolean) => void;
  isLoading: boolean;
  isMobileDetailViewOpen?: boolean;
  onNewRecord?: () => void;
}

export const RecordList: React.FC<RecordListProps> = ({
  records,
  selectedUuid,
  onSelectRecord,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  showArchived,
  onToggleShowArchived,
  isLoading,
  isMobileDetailViewOpen = false,
  onNewRecord,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Debounce wpisywania w wyszukiwarkę (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  const renderEmptyMessage = () => {
    if (searchQuery && searchQuery.trim().length > 0) {
      return 'Brak wyników wyszukiwania';
    }
    if (statusFilter === 'ACTIVE') {
      return 'Brak aktywnych zgłoszeń';
    }
    if (statusFilter === 'ALL') {
      return 'Brak zgłoszeń serwisowych w bazie D1';
    }
    const statusLabel = STATUS_MAP[statusFilter as ServiceRecordStatus]?.label || statusFilter;
    return `Brak zgłoszeń o statusie: ${statusLabel}`;
  };

  return (
    <div className={`sidebar-panel ${isMobileDetailViewOpen ? 'hidden-mobile' : ''}`}>
      <div className="search-filter-box">
        {onNewRecord && (
          <div style={{ marginBottom: '10px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onNewRecord}
              style={{ width: '100%', padding: '8px 14px', fontWeight: 600, fontSize: '0.85rem' }}
            >
              ➕ Nowe zgłoszenie
            </button>
          </div>
        )}
        <div className="search-input-wrapper">
          <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Szukaj (nr, klient, tel, marka, sn)..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </div>
        <StatusFilter
          currentStatus={statusFilter}
          onSelectStatus={onStatusFilterChange}
          showArchived={showArchived}
          onToggleShowArchived={onToggleShowArchived}
        />
      </div>

      <div className="record-list-scroll">
        {isLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Ładowanie zgłoszeń...
          </div>
        ) : records.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
            {renderEmptyMessage()}
          </div>
        ) : (
          records.map((rec) => {
            const statusInfo = STATUS_MAP[rec.status] || { label: rec.status, color: '#000', bgColor: '#eee' };
            const deviceTitle = [rec.brand, rec.model].filter(Boolean).join(' ') || rec.deviceType;
            const isSelected = selectedUuid === rec.uuid;
            const isConflict = (rec as any).centralSyncStatus === 'CONFLICT';

            return (
              <div
                key={rec.uuid}
                className={`record-item-card ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectRecord(rec)}
              >
                <div className="record-item-top">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="ticket-number-badge">{rec.ticketNumber}</span>
                    {isConflict && (
                      <span className="conflict-badge-mini" title="Wykryto konflikt OCC">
                        ⚠️ KONFLIKT
                      </span>
                    )}
                  </div>
                  <span
                    className="status-pill"
                    style={{ color: statusInfo.color, backgroundColor: statusInfo.bgColor }}
                  >
                    {statusInfo.label}
                  </span>
                </div>
                <div className="record-item-client">{rec.clientName}</div>
                <div className="record-item-device">
                  <span>{rec.clientPhone} • {deviceTitle}</span>
                  {rec.priceCents != null && rec.priceCents > 0 && (
                    <span className="record-item-price">{formatPricePLN(rec.priceCents)}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
